import React from 'react'
import Typography from '@mui/material/Typography'
import { Link } from 'react-router'
import Tooltip from '@mui/material/Tooltip'
import VerifiedIcon from '@mui/icons-material/Verified'
import ErrorIcon from '@mui/icons-material/Error'
import { formatNumber } from '../../../../packages/utils/numberFormatters'
import { WarrantName } from '../../../../components/entities/WarrantName'
import { useTranslation } from 'react-i18next'
import { FormIssueEntity } from '../../types/FormIssueEntity'

const getCalculatedNumberOfWarrants = (
    issue: Pick<FormIssueEntity, 'type' | 'linkedIssues' | 'overAllotmentAllocation' | 'unitSubTotal'>,
    warrant: NonNullable<FormIssueEntity['warrants']>[number],
) => {
    switch (issue.type) {
        case 'Guarantor fee issue': {
            const linkedRightsIssue = issue.linkedIssues
                .map((li) =>
                    li.issues?.find(
                        (i) =>
                            ['Rights issue', 'IPO'].includes(i.type) && i.warrants?.find((_w) => _w.id === warrant.id),
                    ),
                )
                .filter((i) => !!i)
                .pop()
            const warrantsPerUnit = linkedRightsIssue?.warrants?.find((_w) => _w.id === warrant.id)?.warrantsPerUnit

            const calculatedUnitsCount =
                ((issue.unitSubTotal ?? 0) * (warrantsPerUnit ?? 0)) / (linkedRightsIssue?.sharesInUnit ?? 1)

            return calculatedUnitsCount
        }
        case 'Rights issue':
        case 'IPO': {
            return ((issue.overAllotmentAllocation ?? 0) + (issue.unitSubTotal ?? 0)) * (warrant.warrantsPerUnit ?? 0)
        }
        default:
            return null
    }
}

const formatNumberWithoutDecimals = (number: string | number | null, locale: string) =>
    formatNumber(number, { decimals: 0, locale })

const WarrantRow = ({ warrant }: { warrant: WarrantWithCalculations }) => {
    const {
        i18n: { language: locale },
    } = useTranslation()
    const errorMsg = warrant.isConnected
        ? `Antalet optioner är angett ${formatNumberWithoutDecimals(warrant.numberOfWarrants, locale)}, vilket skiljer sig från beräkningen ${formatNumberWithoutDecimals(warrant.calculatedUnitsCount, locale)}`
        : `Teckningsoptionen är inte kopplad till denna emission.`

    return (
        <Typography key={warrant.id} variant='body1'>
            <Link to={`/admin/warrants/${warrant.id}`} target='_blank'>
                <WarrantName warrant={warrant} />
            </Link>{' '}
            {warrant.hasIssueTypeUnits &&
                !!warrant.numberOfWarrants &&
                !!warrant.calculatedUnitsCount &&
                (warrant.numberOfWarrants === warrant.calculatedUnitsCount ? (
                    <Tooltip
                        title={`Antalet optioner är angett ${formatNumberWithoutDecimals(warrant.numberOfWarrants, locale)}, vilket stämmer med det beräknade antalet optioner.`}
                    >
                        <VerifiedIcon fontSize='24px' color='success' />
                    </Tooltip>
                ) : (
                    <Tooltip title={errorMsg}>
                        <ErrorIcon fontSize='24' color='error' />
                    </Tooltip>
                ))}
        </Typography>
    )
}

type WarrantWithCalculations = NonNullable<FormIssueEntity['warrants']>[number] & {
    calculatedUnitsCount: number | null
    numberOfWarrants: number | null
    isConnected: boolean
    hasIssueTypeUnits: boolean
}

export const WarrantDetails = ({ issue }: { issue: FormIssueEntity }) => {
    const hasIssueTypeUnits = ['Rights issue', 'Guarantor fee issue', 'IPO'].includes(issue.type)
    const warrants = issue.warrants?.map((w) => ({
        ...w,
        calculatedUnitsCount: getCalculatedNumberOfWarrants(issue, w),
        numberOfWarrants: w.origins?.find((o) => o.issueId === issue.id)?.numberOfWarrants ?? null,
        isConnected: true,
        hasIssueTypeUnits,
    }))
    const linkedRightsIssue = issue.linkedIssues
        .map((li) => li.issues.find((i) => i.type === 'Rights issue'))
        .filter((i) => !!i)
        .pop()
    const potentialWarrants = linkedRightsIssue?.warrants
        ?.filter((w) => !issue.warrants?.find((_w) => _w.id === w.id))
        .map((w) => ({
            ...w,
            calculatedUnitsCount: getCalculatedNumberOfWarrants(issue, w),
            numberOfWarrants: null,
            isConnected: false,
            hasIssueTypeUnits,
        })) // Warrants not connected to issue, but probably should be

    return (
        <>
            {[...(warrants || []), ...(potentialWarrants || [])].map((warrant) => (
                <WarrantRow key={warrant.id} warrant={warrant} />
            ))}
        </>
    )
}
